import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from 'react';
import styles from './FinancialPoaPreparation.module.scss';

type QuickFact = {
  label: string;
  value: string;
  helper: string;
};

type PrepCard = {
  title: string;
  description?: string;
  items: string[];
};

type PrepSlide = {
  id: string;
  label: string;
  heading: string;
  description: string;
  render: () => JSX.Element;
};

const quickFacts: QuickFact[] = [
  {
    label: 'Time needed',
    value: 'About 15 minutes',
    helper: 'Keep names, addresses, and choices nearby so you can move straight through the form.',
  },
  {
    label: 'You’ll confirm',
    value: 'Who acts and how',
    helper: 'List your agent, any backups, and the money tasks each can handle.',
  },
  {
    label: 'Best next step',
    value: 'Talk with your agent now',
    helper: 'Align on expectations, start date, and limits before you generate the final document.',
  },
];

const detailCards: PrepCard[] = [
  {
    title: 'People to list',
    description: 'Gather everyone’s details before you begin.',
    items: [
      'You (the principal): legal name, home address, phone, and optional email.',
      'Primary agent: full name and contact details so institutions can reach them.',
      'Back-up agents (optional): names, contact info, and serving order.',
    ],
  },
  {
    title: 'Authority to grant',
    description: 'Decide what help you want and where to set guardrails.',
    items: [
      'Everyday finances like paying bills, banking, or handling investments.',
      'Specific exclusions or limits to keep certain accounts or actions off the table.',
      'Special instructions that clarify how your agent should step in.',
    ],
  },
];

const decisionCards: PrepCard[] = [
  {
    title: 'When their authority starts',
    items: [
      'Let the agent act right away.',
      'Delay authority until you cannot make decisions yourself.',
    ],
  },
  {
    title: 'How long it lasts',
    items: [
      'Keep the power open-ended until you revoke it.',
      'Add an end date or condition for built-in limits.',
    ],
  },
  {
    title: 'Back-up coordination',
    items: [
      'Choose if backups act one at a time or together.',
      'Add notes they should follow when stepping in.',
    ],
  },
];

const proTips: string[] = [
  'Share expectations with your agent before you finalize the document.',
  'Plan for Maine notary or witness needs so signing day is simple.',
  'Store the finished PDF safely and tell loved ones where it lives.',
];

const nextSteps: string[] = [
  'Download a ready-to-sign PDF the moment you complete the form.',
  'Follow the included notarizing or witnessing instructions to make it legally valid.',
];

const FinancialPoaPreparation = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const pointerStartX = useRef<number | null>(null);

  const slides = useMemo<PrepSlide[]>(
    () => [
      {
        id: 'at-a-glance',
        label: 'At a glance',
        heading: 'Your at-a-glance plan',
        description:
          'These checkpoints cover the whole form. Review them now to move from first question to final signature with confidence.',
        render: () => (
          <div className={styles.slideQuickFacts}>
            <div className={styles.quickGrid}>
              {quickFacts.map((fact) => (
                <article key={fact.label} className={styles.quickCard}>
                  <span className={styles.quickLabel}>{fact.label}</span>
                  <p className={styles.quickValue}>{fact.value}</p>
                  <p className={styles.quickHelper}>{fact.helper}</p>
                </article>
              ))}
            </div>
          </div>
        ),
      },
      {
        id: 'gather-details',
        label: 'Gather details',
        heading: 'Gather these details first',
        description: 'Pull everything together so you can answer each prompt once.',
        render: () => (
          <div className={styles.cardGrid}>
            {detailCards.map((card) => (
              <article key={card.title} className={styles.contentCard}>
                <h3>{card.title}</h3>
                {card.description ? <p>{card.description}</p> : null}
                <ul>
                  {card.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        ),
      },
      {
        id: 'make-decisions',
        label: 'Decisions',
        heading: 'Decisions you’ll lock in',
        description: 'Clarify these choices now so you can confirm them quickly in the form.',
        render: () => (
          <div className={styles.cardGrid}>
            {decisionCards.map((card) => (
              <article key={card.title} className={styles.contentCard}>
                <h3>{card.title}</h3>
                <ul>
                  {card.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        ),
      },
      {
        id: 'final-steps',
        label: 'Finalization',
        heading: 'Final touches and next steps',
        description: 'Give everyone clarity now so signing day feels effortless.',
        render: () => (
          <div className={styles.sideBySide}>
            <article className={styles.noteCard} aria-labelledby="financial-poa-tips">
              <h3 id="financial-poa-tips">Pro tips for peace of mind</h3>
              <ul>
                {proTips.map((tip) => (
                  <li key={tip}>{tip}</li>
                ))}
              </ul>
            </article>
            <article className={styles.noteCard} aria-labelledby="financial-poa-next">
              <h3 id="financial-poa-next">After you finish</h3>
              <ul>
                {nextSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ul>
            </article>
          </div>
        ),
      },
    ],
    []
  );

  const slideCount = slides.length;

  const goToIndex = (index: number) => {
    setActiveIndex((index + slideCount) % slideCount);
  };

  const goToPrevious = () => {
    setActiveIndex((current) => (current === 0 ? slideCount - 1 : current - 1));
  };

  const goToNext = () => {
    setActiveIndex((current) => (current === slideCount - 1 ? 0 : current + 1));
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointerStartX.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const resetPointerTracking = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pointerStartX.current = null;
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerStartX.current === null) {
      return;
    }

    const deltaX = event.clientX - pointerStartX.current;

    if (Math.abs(deltaX) > 40) {
      if (deltaX < 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }

    resetPointerTracking(event);
  };

  const handlePointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    resetPointerTracking(event);
  };

  const handlePointerLeave = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerStartX.current === null) {
      return;
    }

    handlePointerUp(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goToNext();
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goToPrevious();
    }
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <p className={styles.eyebrow}>Financial Power of Attorney</p>
        <h1 className={styles.heroTitle}>Getting Started</h1>
        <p className={styles.heroSubtitle}>
          Spend a few focused minutes gathering details so you can finish your
          Maine financial power of attorney in one pass.
        </p>
      </section>

      <section
        id="financial-poa-carousel"
        className={styles.carouselSection}
        aria-label="Financial power of attorney preparation slideshow"
      >

        <div className={styles.carousel}>
          <button
            type="button"
            className={styles.carouselNav}
            onClick={goToPrevious}
            aria-label="View previous prep slide"
          >
            <span aria-hidden="true">←</span>
          </button>

          <div
            className={styles.carouselViewport}
            role="region"
            aria-roledescription="carousel"
            aria-labelledby={`slide-${slides[activeIndex].id}`}
          >
            <div
              className={styles.carouselTrack}
              role="group"
              tabIndex={0}
              onKeyDown={handleKeyDown}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerLeave}
              onPointerCancel={handlePointerCancel}
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {slides.map((slide, index) => (
                <div key={slide.id} className={styles.slideWrapper}>
                  <article
                    id={`slide-${slide.id}`}
                    className={styles.slide}
                    aria-hidden={index !== activeIndex}
                  >
                    <header className={styles.slideHeader}>
                      <span className={styles.slideLabel}>{slide.label}</span>
                      <h3>{slide.heading}</h3>
                      <p>{slide.description}</p>
                    </header>
                    <div className={styles.slideContent}>{slide.render()}</div>
                  </article>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            className={styles.carouselNav}
            onClick={goToNext}
            aria-label="View next prep slide"
          >
            <span aria-hidden="true">→</span>
          </button>
        </div>

        <div className={styles.carouselDots} role="tablist" aria-label="Preparation sections">
          {slides.map((slide, index) => {
            const isActive = index === activeIndex;

            return (
              <button
                key={slide.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`slide-${slide.id}`}
                id={`slide-tab-${slide.id}`}
                className={
                  isActive
                    ? `${styles.carouselDot} ${styles.carouselDotActive}`
                    : styles.carouselDot
                }
                onClick={() => goToIndex(index)}
              >
                <span className={styles.srOnly}>Go to {slide.label} slide</span>
              </button>
            );
          })}
        </div>
      </section>

      <div className={styles.carouselActions}>
        <Link to="/poa/financial/form" className={styles.primaryAction}>
          I’m ready — start the form
        </Link>
        <Link to="/" className={styles.secondaryLink}>
          ← Back to document picker
        </Link>
      </div>
    </div>
  );
};

export default FinancialPoaPreparation;
